/**
 * Settings → Mail (admins; logic/mail): hosting domains on Bundle — add one, see the DNS records to publish with
 * their live status (copy buttons on the screen), re-check now, tighten DMARC, remove — and the mailboxes on them
 * (personal or shared, their members). Domain changes also arrive as realtime `mail.domain.updated` (the cron's
 * checks). Online only.
 */
import type { CreateMailGroupRequest, CreateMailboxRequest, MailDomain, MailGroup, Mailbox, MailboxesFor, PersistentEvent, UpdateMailGroupRequest, UpdateMailboxRoutingRequest } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { ApiFailure } from "../ports.ts";
import { isValidLocalPart } from "../logic/mail.ts";
import { normalizeEmailDomain } from "../logic/emailDomains.ts";
import { importDomainSuggestions } from "../logic/mailIdentity.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type MailSettingsState = {
  status: "loading" | "ready" | "failed";
  domains: MailDomain[];
  mailboxes: Mailbox[];
  /** Mail groups (distribution lists): Settings → Email → Groups. */
  groups: MailGroup[];
  /** Domains a Google import chose that the workspace doesn't have yet ("Add"; `aliasOf` is a domain name). */
  suggestedDomains: { domain: string; aliasOf?: string }[];
  /** A request is in flight (add, check, create). */
  busy: boolean;
  error?: MessageDescriptor;
  /**
   * The domain whose removal the server refused because mailboxes still use it as their address (409
   * domain_in_use, as Google Admin does): the remove confirm lists them (`mailboxes` with that `domainId`, reloaded).
   */
  removeBlocked?: string;
};

const sortGroups = (groups: MailGroup[]) => [...groups].sort((a, b) => a.address.localeCompare(b.address));

export class MailSettingsViewModel extends ViewModel<MailSettingsState> {
  private connections: { google?: { domains?: { domain: string; import: boolean; aliasOf?: string | undefined }[] | undefined } | undefined }[] = [];

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "loading", domains: [], mailboxes: [], groups: [], suggestedDomains: [], busy: false });
  }

  private get api() {
    return this.ws.deps.api;
  }

  async refresh(): Promise<void> {
    try {
      const [domains, mailboxes, groups, sync] = await Promise.all([
        this.api.listMailDomains(this.ws.wsId),
        this.api.listMailboxes(this.ws.wsId, true),
        // Optional parts: an older server without groups, or no import, still shows the rest.
        this.api.listMailGroups(this.ws.wsId).catch(() => [] as MailGroup[]),
        this.api.listMailSync(this.ws.wsId).catch(() => ({ connections: [] })),
      ]);
      this.connections = sync.connections;
      if (!this.isDisposed) this.update({ status: "ready", domains, mailboxes, groups: sortGroups(groups), suggestedDomains: importDomainSuggestions(sync.connections, domains.map((d) => d.domain)) });
    } catch {
      if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
    }
  }

  onEvent(ev: PersistentEvent): void {
    if (ev.type === "mail.domain.updated") this.putDomain(ev.domain);
    else if (ev.type === "mail.mailbox.updated" || ev.type === "mail.mailbox.deleted") void this.refresh();
  }

  /** `mailboxes`: who gets a mailbox automatically once the domain can receive mail (asked in the add form). */
  async addDomain(raw: string, mailboxes: MailboxesFor = "me"): Promise<MailDomain | undefined> {
    const domain = normalizeEmailDomain(raw);
    if (domain === undefined) {
      this.update({ error: msg("mail.settings.invalidDomain") });
      return undefined;
    }
    return this.run(() => this.api.addMailDomain(this.ws.wsId, domain, mailboxes), (d) => this.putDomain(d));
  }

  /** Adds a domain the import suggested: an alias one comes with `aliasOf` set to that workspace domain. */
  addSuggestedDomain(domain: string): Promise<MailDomain | undefined> {
    const s = this.state.suggestedDomains.find((x) => x.domain === domain);
    const aliasOf = s?.aliasOf === undefined ? undefined : this.state.domains.find((d) => d.domain === s.aliasOf)?.id;
    return this.run(
      () => this.api.addMailDomain(this.ws.wsId, domain, "none", aliasOf),
      (d) => {
        this.putDomain(d);
        this.update({ suggestedDomains: importDomainSuggestions(this.connections, this.state.domains.map((x) => x.domain)) });
      },
    );
  }

  createGroup(req: CreateMailGroupRequest): Promise<MailGroup | undefined> {
    return this.run(() => this.api.createMailGroup(this.ws.wsId, req), (g) => this.putGroup(g));
  }

  updateGroup(groupId: string, req: UpdateMailGroupRequest): Promise<MailGroup | undefined> {
    return this.run(() => this.api.updateMailGroup(this.ws.wsId, groupId, req), (g) => this.putGroup(g));
  }

  async deleteGroup(groupId: string): Promise<void> {
    await this.run(() => this.api.deleteMailGroup(this.ws.wsId, groupId), () => this.update({ groups: this.state.groups.filter((g) => g.id !== groupId) }));
  }

  private putGroup(g: MailGroup): void {
    this.update({ groups: sortGroups([...this.state.groups.filter((x) => x.id !== g.id), g]) });
  }

  checkDomain(domainId: string): Promise<MailDomain | undefined> {
    return this.run(() => this.api.checkMailDomain(this.ws.wsId, domainId), (d) => this.putDomain(d));
  }

  setDmarc(domainId: string, policy: MailDomain["dmarcPolicy"]): Promise<MailDomain | undefined> {
    return this.run(() => this.api.setMailDomainDmarc(this.ws.wsId, domainId, policy), (d) => this.putDomain(d));
  }

  /**
   * Removes a domain. The server refuses while mailboxes have their address on it (409 domain_in_use): then
   * `removeBlocked` names it and the mailboxes are reloaded, so the confirm can list them. → whether it was removed.
   */
  async removeDomain(domainId: string): Promise<boolean> {
    const { error: _e, removeBlocked: _b, ...rest } = this.state;
    void _e;
    void _b;
    this.setState({ ...rest, busy: true });
    try {
      await this.api.deleteMailDomain(this.ws.wsId, domainId);
      if (!this.isDisposed) this.update({ busy: false, domains: this.state.domains.filter((d) => d.id !== domainId), mailboxes: this.state.mailboxes.filter((m) => m.domainId !== domainId) });
      return true;
    } catch (e) {
      if (this.isDisposed) return false;
      if (e instanceof ApiFailure && e.code === "domain_in_use") {
        const mailboxes = await this.api.listMailboxes(this.ws.wsId, true).catch(() => this.state.mailboxes);
        if (!this.isDisposed) this.update({ busy: false, mailboxes, removeBlocked: domainId });
      } else this.update({ busy: false, error: msg("mail.settings.failed") });
      return false;
    }
  }

  /** Closes the "can't be removed while mailboxes use it" note. */
  clearRemoveBlocked(): void {
    if (this.state.removeBlocked === undefined) return;
    const { removeBlocked: _b, ...rest } = this.state;
    void _b;
    this.setState(rest);
  }

  async createMailbox(req: CreateMailboxRequest): Promise<Mailbox | undefined> {
    if (!isValidLocalPart(req.localPart)) {
      this.update({ error: msg("mail.settings.invalidLocalPart") });
      return undefined;
    }
    return this.run(() => this.api.createMailbox(this.ws.wsId, req), (m) => this.update({ mailboxes: [...this.state.mailboxes.filter((x) => x.id !== m.id), m] }));
  }

  setDomainAlias(domainId: string, aliasOf: string | null): Promise<MailDomain | undefined> {
    return this.run(() => this.api.setMailDomainAlias(this.ws.wsId, domainId, aliasOf), (d) => this.putDomain(d));
  }

  setBimiLogo(domainId: string, svg: string | null): Promise<MailDomain | undefined> {
    return this.run(() => this.api.setMailDomainBimi(this.ws.wsId, domainId, svg), (d) => this.putDomain(d));
  }

  resumeSending(domainId: string): Promise<MailDomain | undefined> {
    return this.run(() => this.api.resumeMailDomainSending(this.ws.wsId, domainId), (d) => this.putDomain(d));
  }

  /** Alias addresses and forwarding of a mailbox (Settings → Email → Users & forwarding). */
  setRouting(mailboxId: string, req: UpdateMailboxRoutingRequest): Promise<Mailbox | undefined> {
    return this.run(() => this.api.updateMailboxRouting(this.ws.wsId, mailboxId, req), (m) => this.update({ mailboxes: this.state.mailboxes.map((x) => (x.id === m.id ? m : x)) }));
  }

  setMailboxMembers(mailboxId: string, memberIds: string[]): Promise<Mailbox | undefined> {
    return this.run(() => this.api.updateMailbox(this.ws.wsId, mailboxId, { memberIds }), (m) => this.update({ mailboxes: this.state.mailboxes.map((x) => (x.id === m.id ? m : x)) }));
  }

  async deleteMailbox(mailboxId: string): Promise<void> {
    await this.run(() => this.api.deleteMailbox(this.ws.wsId, mailboxId), () => this.update({ mailboxes: this.state.mailboxes.filter((m) => m.id !== mailboxId) }));
  }

  private putDomain(d: MailDomain): void {
    const domains = this.state.domains.some((x) => x.id === d.id) ? this.state.domains.map((x) => (x.id === d.id ? d : x)) : [...this.state.domains, d];
    this.update({ domains: domains.sort((a, b) => a.domain.localeCompare(b.domain)) });
  }

  private async run<T>(call: () => Promise<T>, done: (v: T) => void): Promise<T | undefined> {
    const { error: _old, ...rest } = this.state;
    void _old;
    this.setState({ ...rest, busy: true });
    try {
      const v = await call();
      if (!this.isDisposed) {
        done(v);
        this.update({ busy: false });
      }
      return v;
    } catch {
      if (!this.isDisposed) this.update({ busy: false, error: msg("mail.settings.failed") });
      return undefined;
    }
  }
}
